import { OUTPUT_STORAGE_KEY } from "../../config/constants";

// Function to get initial output from localStorage or default
const getInitialOutput = () => {
  if (typeof window !== "undefined") {
    try {
      const savedOutput = localStorage.getItem(OUTPUT_STORAGE_KEY);
      if (savedOutput) {
        return JSON.parse(savedOutput);
      }
    } catch (error) {
      console.error("Error reading output from localStorage:", error);
    }
  }
  return [];
};

export const createOutputSlice = (set, get) => ({
  // Output state
  output: getInitialOutput(),

  // Frame mode — when active, TerminalOutput only renders lines from frameStartIndex onwards.
  // Used by questionnaire flow to show one question at a time.
  frameMode: false,
  frameStartIndex: 0,

  startNewFrame: () => {
    set({ frameMode: true, frameStartIndex: get().output.length });
  },
  endFrameMode: () => {
    set({ frameMode: false, frameStartIndex: 0 });
  },

  // Basic output setter
  setOutput: (newOutput) => {
    const output =
      typeof newOutput === "function" ? newOutput(get().output) : newOutput;

    // Reset wasCleared flag when output is added (but not when cleared)
    if (output.length > 0) {
      set({ output, wasCleared: false });
    } else {
      set({ output });
    }

    // Persist to localStorage
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(OUTPUT_STORAGE_KEY, JSON.stringify(output));
      } catch (error) {
        console.error("Error saving output to localStorage:", error);
      }
    }

    // Auto-scroll to bottom when new content is added
    if (output.length > 0) {
      const { scrollToBottomByFocus } = get();
      if (scrollToBottomByFocus) {
        scrollToBottomByFocus();
      }
    }
  },

  // Enhanced setOutput with delay for system outputs
  setOutputWithDelay: async (newOutput, forceImmediate = false) => {
    const {
      outputDelay,
      setOutput,
      setIsLoading,
      setSkipCurrentTyping,
      setIsSkippableDelay,
    } = get();

    // Handle function-based updates first
    if (typeof newOutput === "function") {
      const currentOutput = get().output;
      const resultOutput = newOutput(currentOutput);
      const newEntries = resultOutput.slice(currentOutput.length);

      // If no new entries or force immediate, just apply normally
      if (newEntries.length === 0 || forceImmediate || outputDelay === 0) {
        setOutput(newOutput);
        return Promise.resolve();
      }

      // Reset skip state at the beginning
      setSkipCurrentTyping(false);
      // Add each new entry with delay
      setIsLoading(true);
      setIsSkippableDelay(true);

      for (let i = 0; i < newEntries.length; i++) {
        const entry = newEntries[i];
        const isSystemOutput =
          (typeof entry === "object" && entry.type === "output") ||
          typeof entry === "string"; // strings are treated as system output

        if (isSystemOutput) {
          // Wait for the delay before adding this entry, but check for skip
          const delayPromise = new Promise((resolve) =>
            setTimeout(resolve, outputDelay)
          );

          const skipPromise = new Promise((resolve) => {
            const checkSkipInterval = setInterval(() => {
              const currentState = get();
              if (currentState.skipCurrentTyping) {
                clearInterval(checkSkipInterval);
                setSkipCurrentTyping(false); // Reset skip flag
                resolve(); // Skip the delay
              }
            }, 10); // Check every 10ms for skip

            // Clean up interval when delay completes normally
            delayPromise.then(() => clearInterval(checkSkipInterval));
          });

          await Promise.race([delayPromise, skipPromise]);
        }

        // Add this single entry
        setOutput((prev) => [...prev, entry]);
      }

      set({ isLoading: false, isSkippableDelay: false });
      return Promise.resolve();
    }

    // Handle direct array or object updates
    const isSystemOutput =
      (typeof newOutput === "object" && newOutput.type === "output") ||
      typeof newOutput === "string" ||
      (Array.isArray(newOutput) &&
        newOutput.some(
          (entry) =>
            (typeof entry === "object" && entry.type === "output") ||
            typeof entry === "string"
        ));

    if (isSystemOutput && !forceImmediate && outputDelay > 0) {
      // Reset skip state at the beginning
      setSkipCurrentTyping(false);
      setIsLoading(true);
      setIsSkippableDelay(true);

      // Create skippable delay
      const delayPromise = new Promise((resolve) =>
        setTimeout(resolve, outputDelay)
      );

      const skipPromise = new Promise((resolve) => {
        const checkSkipInterval = setInterval(() => {
          const currentState = get();
          if (currentState.skipCurrentTyping) {
            clearInterval(checkSkipInterval);
            setSkipCurrentTyping(false); // Reset skip flag
            resolve(); // Skip the delay
          }
        }, 10); // Check every 10ms for skip

        // Clean up interval when delay completes normally
        delayPromise.then(() => clearInterval(checkSkipInterval));
      });

      await Promise.race([delayPromise, skipPromise]);

      setOutput(newOutput);
      set({ isLoading: false, isSkippableDelay: false });
      return Promise.resolve();
    } else {
      // No delay, apply immediately
      setOutput(newOutput);
      return Promise.resolve();
    }
  },

  // Add multiple output lines with delay between each line
  addOutputLinesWithDelay: async (lines, forceImmediate = false) => {
    const {
      outputDelay,
      setOutput,
      setIsLoading,
      setSkipCurrentTyping,
      setIsSkippableDelay,
      scrollToBottomByFocus,
    } = get();

    if (!Array.isArray(lines) || lines.length === 0) {
      return Promise.resolve();
    }

    if (forceImmediate || outputDelay === 0) {
      setOutput((prev) => [...prev, ...lines]);
      // Auto-scroll after adding all lines
      if (scrollToBottomByFocus) {
        scrollToBottomByFocus();
      }
      return Promise.resolve();
    }

    // Reset skip state at the beginning
    setSkipCurrentTyping(false);
    setIsLoading(true);
    setIsSkippableDelay(true);

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isSystemOutput =
        (typeof line === "object" && line.type === "output") ||
        typeof line === "string";

      if (isSystemOutput && i > 0) {
        // Wait for delay before adding non-first system output lines, but check for skip
        const delayPromise = new Promise((resolve) =>
          setTimeout(resolve, outputDelay)
        );

        const skipPromise = new Promise((resolve) => {
          const checkSkipInterval = setInterval(() => {
            const currentState = get();
            if (currentState.skipCurrentTyping) {
              clearInterval(checkSkipInterval);
              setSkipCurrentTyping(false); // Reset skip flag
              resolve(); // Skip the delay
            }
          }, 10); // Check every 10ms for skip

          // Clean up interval when delay completes normally
          delayPromise.then(() => clearInterval(checkSkipInterval));
        });

        await Promise.race([delayPromise, skipPromise]);
      }

      // Add this single line
      setOutput((prev) => [...prev, line]);

      // Auto-scroll after each line is added
      if (scrollToBottomByFocus) {
        scrollToBottomByFocus();
      }
    }

    set({ isLoading: false, isSkippableDelay: false });
    return Promise.resolve();
  },

  // Add output with character-by-character typing effect
  addOutputWithTypingEffect: async (lines, forceImmediate = false) => {
    const {
      outputDelay,
      typingSpeed,
      setOutput,
      setIsTyping,
      setSkipCurrentTyping,
      setIsSkippableDelay,
      scrollToBottomByFocus,
    } = get();

    if (!Array.isArray(lines) || lines.length === 0) {
      return Promise.resolve();
    }

    // Snapshot resetToken so we can abort cleanly if the host calls
    // incrementResetToken() (e.g. on lesson unmount or terminal reset).
    const initialResetToken = get().resetToken;
    const isAborted = () => get().resetToken !== initialResetToken;

    // Reset skip state at the beginning
    setSkipCurrentTyping(false);

    // If typing speed is 0, disable character typing but still use line delays if outputDelay > 0
    if (typingSpeed === 0) {
      if (outputDelay > 0 && !forceImmediate) {
        // Use line-by-line delay without character typing
        const { addOutputLinesWithDelay } = get();
        return addOutputLinesWithDelay(lines, forceImmediate);
      } else {
        // Add all lines immediately
        setOutput((prev) => [...prev, ...lines]);
        // Clear typing flags so the prompt/input field can reappear
        // (callers like handleSessionInput pre-set isTyping=true for
        // visual reasons; with speed=0 we must clear it ourselves).
        setIsTyping(false);
        set({ isLoading: false, isSkippableDelay: false });
        if (scrollToBottomByFocus) {
          scrollToBottomByFocus();
        }
        return Promise.resolve();
      }
    }

    // If outputDelay is 0, skip the typing effect and add all lines immediately
    if (forceImmediate || outputDelay === 0) {
      setOutput((prev) => [...prev, ...lines]);
      // Auto-scroll after adding all lines
      if (scrollToBottomByFocus) {
        scrollToBottomByFocus();
      }
      return Promise.resolve();
    }

    setIsTyping(true); // Use typing state instead of loading state

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      if (isAborted()) return Promise.resolve();
      const line = lines[lineIndex];
      const isSystemOutput =
        (typeof line === "object" && line.type === "output") ||
        typeof line === "string";

      if (isSystemOutput) {
        // For system output, add typing effect
        const rawText = typeof line === "string" ? line : line.text;

        // Rich text support: line.text may be an array of styled segments
        // e.g. [{ text: "━━ Title ━━", color: "#fff", bg: "#333" }]
        // Extract the plain text for character-by-character typing and
        // reconstruct the rich structure progressively.
        const isRichText = Array.isArray(rawText);
        const plainText = isRichText
          ? rawText.map((seg) => (typeof seg === "string" ? seg : seg.text || "")).join("")
          : rawText;
        // Build the full rich text value to use when completing instantly
        const fullText = isRichText ? rawText : plainText;

        if (plainText && plainText.length > 0) {
          // Create a partial entry that will be updated character by character
          const emptyText = isRichText ? "" : "";
          const lineStructure =
            typeof line === "string"
              ? { type: "output", text: emptyText }
              : { ...line, text: emptyText };

          // Add the empty line first and auto-scroll when new line begins
          const linePosition = await new Promise((resolve) => {
            setOutput((prev) => {
              const newOutput = [...prev, lineStructure];
              resolve(newOutput.length - 1); // Return the index of the new line
              return newOutput;
            });
          });

          // Clear skippable delay state immediately after adding the line to DOM
          if (lineIndex > 0) {
            setIsSkippableDelay(false);
          }

          // Auto-scroll when new line begins typing
          if (scrollToBottomByFocus) {
            scrollToBottomByFocus();
          }

          // Helper: build the text value for a given number of typed characters
          const buildPartialText = (charCount) => {
            if (!isRichText) return plainText.substring(0, charCount);
            // For rich text, distribute characters across segments
            let remaining = charCount;
            return rawText.map((seg) => {
              const segText = typeof seg === "string" ? seg : seg.text || "";
              const take = Math.min(remaining, segText.length);
              remaining -= take;
              if (typeof seg === "string") return segText.substring(0, take);
              return { ...seg, text: segText.substring(0, take) };
            });
          };

          // Add characters one by one
          for (let charIndex = 0; charIndex < plainText.length; charIndex++) {
            // Aborted by host (lesson unmount / reset) — bail without writing
            if (isAborted()) return Promise.resolve();
            // Check if we should skip this line
            const currentState = get();
            if (currentState.skipCurrentTyping) {
              // Complete the current line immediately
              setOutput((prev) => {
                const newOutput = [...prev];
                newOutput[linePosition] = {
                  ...newOutput[linePosition],
                  text: fullText, // Set full text
                };
                return newOutput;
              });
              // Reset skip flag for next line
              setSkipCurrentTyping(false);
              break; // Exit character loop for this line
            }

            await new Promise((resolve) => setTimeout(resolve, typingSpeed));
            if (isAborted()) return Promise.resolve();

            const partialText = buildPartialText(charIndex + 1);
            setOutput((prev) => {
              const newOutput = [...prev];
              newOutput[linePosition] = {
                ...newOutput[linePosition],
                text: partialText,
              };
              return newOutput;
            });

            // Sentence pause: extra delay after sentence-ending punctuation
            // The pause character blinks on/off while waiting.
            const { sentencePause } = get();
            if (sentencePause > 0) {
              const ch = plainText[charIndex];
              const next = plainText[charIndex + 1];
              // Pause after . ! ? ; ━ when followed by space or end of text
              if (/[.!?;·━]/.test(ch) && (!next || /\s/.test(next))) {
                if (!get().skipCurrentTyping) {
                  const charsTyped = charIndex + 1;
                  const textWithChar = buildPartialText(charsTyped);
                  const textWithoutChar = buildPartialText(charsTyped - 1);
                  let visible = true;
                  const blinkInterval = setInterval(() => {
                    if (get().skipCurrentTyping || isAborted()) return;
                    visible = !visible;
                    setOutput((prev) => {
                      const newOutput = [...prev];
                      newOutput[linePosition] = {
                        ...newOutput[linePosition],
                        text: visible ? textWithChar : textWithoutChar,
                      };
                      return newOutput;
                    });
                  }, 400);
                  await new Promise((resolve) => {
                    const skipCheck = setInterval(() => {
                      if (get().skipCurrentTyping || isAborted()) {
                        clearInterval(skipCheck);
                        resolve();
                      }
                    }, 50);
                    setTimeout(() => { clearInterval(skipCheck); resolve(); }, sentencePause);
                  });
                  clearInterval(blinkInterval);
                  if (isAborted()) return Promise.resolve();
                  // Ensure character is visible after blink
                  setOutput((prev) => {
                    const newOutput = [...prev];
                    newOutput[linePosition] = {
                      ...newOutput[linePosition],
                      text: textWithChar,
                    };
                    return newOutput;
                  });
                }
              }
            }

            // Auto-scroll after every few characters to avoid excessive scrolling
            if (charIndex % 8 === 0 || charIndex === plainText.length - 1) {
              if (scrollToBottomByFocus) {
                scrollToBottomByFocus();
              }
            }
          }
        } else {
          // Empty line, just add it
          setOutput((prev) => [...prev, line]);

          // Clear skippable delay state immediately after adding empty line to DOM
          if (lineIndex > 0) {
            setIsSkippableDelay(false);
          }

          // Auto-scroll after adding empty line
          if (scrollToBottomByFocus) {
            scrollToBottomByFocus();
          }
        }

        // Wait for line delay before next line (except for first line)
        if (lineIndex < lines.length - 1) {
          // Set skippable delay state to show "..." during line delays
          setIsSkippableDelay(true);

          // Create skippable delay for line spacing
          const delayPromise = new Promise((resolve) =>
            setTimeout(resolve, outputDelay)
          );

          const skipPromise = new Promise((resolve) => {
            const checkSkipInterval = setInterval(() => {
              const currentState = get();
              if (currentState.skipCurrentTyping || isAborted()) {
                clearInterval(checkSkipInterval);
                if (!isAborted()) setSkipCurrentTyping(false); // Reset skip flag
                resolve(); // Skip the delay
              }
            }, 10); // Check every 10ms for skip

            // Clean up interval when delay completes normally
            delayPromise.then(() => clearInterval(checkSkipInterval));
          });

          await Promise.race([delayPromise, skipPromise]);
          if (isAborted()) return Promise.resolve();
        }
      } else {
        // Non-system output (user input), add immediately
        setOutput((prev) => [...prev, line]);
      }
    }

    if (isAborted()) return Promise.resolve();
    // Clear all visual-state flags in a single store update to prevent an
    // intermediate render where isLoading is still true after typing ends
    // (which briefly flashes the loading indicator before the prompt appears).
    set({ isTyping: false, isSkippableDelay: false, isLoading: false });

    return Promise.resolve();
  },
});
